import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PrimaryButton } from '@components/Button/variants';
import LazyImgixImage from '@components/LazyImgixImage';
import SectionBreakTitle, { SectionColor } from '@components/SectionBreakTitle';
import useScrollToHash from '@hooks/useScrollToHash';
import { getImgixSrc } from '@utils/images';

import { PAST_SPEAKERS, SPEAKER_FORM_URL } from './constant';

const SCROLL_END_THRESHOLD_PX = 4;
const MOBILE_SPEAKERS_PER_PAGE = 4;

type PastSpeakerEntry = (typeof PAST_SPEAKERS)[number];

const SpeakerCell = ({ speaker }: { speaker: PastSpeakerEntry }) => (
  <div className="min-h-[470px]">
    <LazyImgixImage
      src={getImgixSrc({ path: speaker.image, isEnvDependent: false })}
      alt={speaker.name}
      className="h-full w-full object-cover"
    />
    <div className="p-16">
      <h4 className="m-0 font-suisse-works text-headline-xxs text-black">{speaker.name}</h4>
      <span className="mt-4 font-suisse-intl text-button-md text-grayscale-gray">
        {speaker.title}
      </span>
    </div>
  </div>
);

const PastSpeaker = () => {
  const scrollRefMobile = useRef<HTMLDivElement>(null);
  const scrollRefDesktop = useRef<HTMLDivElement>(null);
  const [showScrollFade, setShowScrollFade] = useState(true);

  const speakerPages = useMemo(() => {
    const pages: PastSpeakerEntry[][] = [];
    for (let i = 0; i < PAST_SPEAKERS.length; i += MOBILE_SPEAKERS_PER_PAGE) {
      pages.push(PAST_SPEAKERS.slice(i, i + MOBILE_SPEAKERS_PER_PAGE));
    }
    return pages;
  }, []);

  const updateScrollFade = useCallback(() => {
    const el = scrollRefDesktop.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const distanceFromEnd = scrollHeight - scrollTop - clientHeight;
    const canScroll = scrollHeight > clientHeight + 1;
    setShowScrollFade(canScroll && distanceFromEnd > SCROLL_END_THRESHOLD_PX);
  }, []);
  const pastSpeakersRef = useScrollToHash({ hash: '#speakers', offset: 100 });

  useEffect(() => {
    updateScrollFade();
    const ro = new ResizeObserver(() => updateScrollFade());
    const desktopEl = scrollRefDesktop.current;
    if (desktopEl) ro.observe(desktopEl);
    window.addEventListener('resize', updateScrollFade);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateScrollFade);
    };
  }, [updateScrollFade]);

  return (
    <div className="px-12">
      <div className="m-auto max-w-container-lg">
        <SectionBreakTitle
          className="mb-32"
          titleClassName="text-black"
          color={SectionColor.Gray}
          title="Past speakers"
        />
        <div
          id="speakers"
          className={clsx(
            'relative',
            showScrollFade &&
              "after:pointer-events-none after:absolute after:bottom-0 after:left-0 after:z-10 after:hidden after:h-[120px] after:w-full after:bg-[linear-gradient(to_top,#fff_0%,rgba(255,255,255,0)_100%)] after:content-[''] sm:after:block"
          )}
          ref={pastSpeakersRef}
        >
          <div
            ref={scrollRefMobile}
            className="max-sm:flex max-sm:snap-x max-sm:snap-mandatory max-sm:overflow-x-auto max-sm:pb-32 sm:hidden"
          >
            {speakerPages.map((page, pageIndex) => (
              <div
                key={pageIndex}
                className={clsx(
                  'w-full min-w-full shrink-0 snap-start',
                  page.length === 2
                    ? 'flex flex-col items-center gap-y-40'
                    : 'grid grid-cols-2 gap-x-0 gap-y-40'
                )}
              >
                {page.map((speaker) => (
                  <div
                    key={`${speaker.image}-${speaker.name}`}
                    className={clsx(page.length === 2 && 'w-1/2 min-w-0')}
                  >
                    <SpeakerCell speaker={speaker} />
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div
            ref={scrollRefDesktop}
            onScroll={updateScrollFade}
            className="hidden max-h-[950px] grid-cols-4 gap-y-40 overflow-y-scroll pb-32 sm:grid"
          >
            {PAST_SPEAKERS.map((speaker) => (
              <SpeakerCell key={`${speaker.image}-${speaker.name}`} speaker={speaker} />
            ))}
          </div>
        </div>
        <aside className="mt-48 flex flex-col items-center justify-center gap-24 border border-solid border-grayscale-gray px-72 py-48 sm:mt-64 sm:py-64">
          <p className="m-0 p-0 text-center font-suisse-intl-mono text-caption-lg font-normal text-primary-black sm:text-feed-headline-sm">
            To submit a speaker, select the relevant event page above. For events not listed, use
            our general speaker application here.
          </p>
          <PrimaryButton
            as="link"
            target="_blank"
            href={SPEAKER_FORM_URL}
            className="max-w-[200px]"
          >
            Become a speaker
          </PrimaryButton>
        </aside>
      </div>
    </div>
  );
};

export default PastSpeaker;
